/**
 * Loads third-party tags, and only the ones the visitor has consented to.
 *
 * Nothing in here runs until `onConsentChange` reports a granted category, so a
 * visitor who refuses — or who has not answered yet — gets no analytics, no
 * session recording and no marketing tags at all.
 *
 * A tag cannot be un-loaded once it is on the page, so withdrawing consent
 * purges the third-party cookies and reloads: that is the only way to make a
 * withdrawal take effect immediately rather than at the next navigation.
 */
import { CONSENT_CATEGORIES, onConsentChange } from './cookie-consent'

const loaded = {
  matomo: false,
  googleTagManager: false,
  hotjar: false,
  ecosystemScripts: false,
}

/**
 * Cookies dropped by the tags we load, grouped by the consent category that
 * allows them. Anything listed under a category the visitor has not granted is
 * cleared from the browser.
 */
const COOKIE_PREFIXES_BY_CATEGORY = {
  [CONSENT_CATEGORIES.FUNCTIONAL]: [
    // Userback, and the Mixpanel instance it runs on its own behalf
    'userback_', 'ubdi', 'mp_',
  ],
  [CONSENT_CATEGORIES.ANALYTICS]: [
    // Matomo (`_pk_testcookie` is covered by `_pk_`)
    '_pk_',
    // Hotjar
    '_hj',
  ],
  [CONSENT_CATEGORIES.MARKETING]: [
    // Google Analytics / Google Ads, loaded via tag manager containers
    '_ga', '_gid', '_gat', '_gcl',
  ],
}

/**
 * Cookies matched on their exact name rather than a prefix. `ct` is Userback's,
 * and it is short enough that prefix-matching it would take unrelated cookies
 * with it.
 */
const COOKIE_NAMES_BY_CATEGORY = {
  [CONSENT_CATEGORIES.FUNCTIONAL]: ['ct'],
}

function trackingConfig () {
  return window.trackingConfig || {}
}

function injectScript (src) {
  const script = document.createElement('script')
  script.async = true
  script.src = src

  const first = document.getElementsByTagName('script')[0]

  if (first && first.parentNode) {
    first.parentNode.insertBefore(script, first)
  } else {
    document.head.appendChild(script)
  }

  return script
}

function loadMatomo () {
  const matomo = trackingConfig().matomo

  if (loaded.matomo || !matomo || !matomo.hostUrl || !matomo.siteId) {
    return
  }

  loaded.matomo = true

  const paq = window._paq = window._paq || []
  const host = matomo.hostUrl

  if (matomo.userId) {
    paq.push(['setUserId', String(matomo.userId)])
  }

  paq.push(['setTrackerUrl', host + 'matomo.php'])
  paq.push(['setSiteId', String(matomo.siteId)])

  if (matomo.allEcosystemsSiteId) {
    paq.push(['addTracker', host + 'matomo.php', String(matomo.allEcosystemsSiteId)])
  }

  paq.push(['trackPageView'])
  paq.push(['enableLinkTracking'])
  paq.push(['trackVisibleContentImpressions'])

  injectScript(host + 'matomo.js')
}

function loadGoogleTagManager () {
  const containers = trackingConfig().googleTagManager || []

  if (loaded.googleTagManager || !containers.length) {
    return
  }

  loaded.googleTagManager = true

  window.dataLayer = window.dataLayer || []

  containers.forEach(containerId => {
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
    injectScript('https://www.googletagmanager.com/gtm.js?id=' + encodeURIComponent(containerId))
  })
}

function loadHotjar () {
  const hotjar = trackingConfig().hotjar

  if (loaded.hotjar || !hotjar || !hotjar.id) {
    return
  }

  loaded.hotjar = true

  window.hj = window.hj || function () {
    (window.hj.q = window.hj.q || []).push(arguments)
  }
  window._hjSettings = { hjid: hotjar.id, hjsv: 6 }

  injectScript('https://static.hotjar.com/c/hotjar-' + hotjar.id + '.js?sv=6')

  if (hotjar.identify && hotjar.identify.userId) {
    window.hj('identify', hotjar.identify.userId, {
      user_id: hotjar.identify.userId,
      email: hotjar.identify.email,
      signed_up: hotjar.identify.signedUp,
      role: hotjar.identify.role,
    })
  }
}

/**
 * Per-ecosystem tracking snippets configured by ecosystem owners. Treated as
 * marketing because we cannot know what an arbitrary snippet does.
 */
function loadEcosystemScripts () {
  const snippets = trackingConfig().ecosystemScripts || []

  if (loaded.ecosystemScripts || !snippets.length) {
    return
  }

  loaded.ecosystemScripts = true

  snippets.forEach(html => {
    const template = document.createElement('template')
    template.innerHTML = html

    Array.from(template.content.childNodes).forEach(node => {
      if (node.tagName !== 'SCRIPT') {
        document.body.appendChild(node.cloneNode(true))

        return
      }

      // A script inserted via innerHTML never executes; it has to be recreated.
      const script = document.createElement('script')

      Array.from(node.attributes).forEach(attribute => {
        script.setAttribute(attribute.name, attribute.value)
      })

      script.text = node.textContent
      document.body.appendChild(script)
    })
  })
}

function deleteCookies ({ prefixes, names }) {
  if (!prefixes.length && !names.length) {
    return
  }

  const hostname = window.location.hostname
  const domains = [null, hostname, '.' + hostname]

  // Also try the registrable domain, since Matomo and tag managers set cookies
  // there rather than on the exact host.
  const parts = hostname.split('.')

  if (parts.length > 2) {
    domains.push('.' + parts.slice(-2).join('.'))
  }

  document.cookie.split(';').forEach(entry => {
    const name = entry.split('=')[0].trim()

    const matches = names.includes(name) || prefixes.some(prefix => name.startsWith(prefix))

    if (!name || !matches) {
      return
    }

    domains.forEach(domain => {
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/' +
        (domain ? '; domain=' + domain : '')
    })
  })
}

/** Cookies belonging to every category the visitor has not granted. */
function ungrantedCookies (granted) {
  const ungranted = category => !granted[category]

  return {
    prefixes: Object.entries(COOKIE_PREFIXES_BY_CATEGORY)
      .filter(([category]) => ungranted(category))
      .flatMap(([, prefixes]) => prefixes),
    names: Object.entries(COOKIE_NAMES_BY_CATEGORY)
      .filter(([category]) => ungranted(category))
      .flatMap(([, names]) => names),
  }
}

/**
 * Cookies set before this consent gate existed (or by a tag the visitor has
 * since refused) must not be left sitting in the browser. Clearing them once on
 * load is what makes a refusal apply to visitors who were already tracked.
 */
function purgeUngrantedCookies (granted) {
  deleteCookies(ungrantedCookies(granted))
}

/** Categories for which we have actually put tags on the page. */
const loadedCategories = new Set()

/**
 * Register a tag loaded elsewhere (the Userback widget lives in main.ts, since
 * it needs the Vue app). Without this the loader would not know the category is
 * live, and withdrawing consent for it would leave the tag running until the
 * next navigation.
 */
export function markCategoryLoaded (category) {
  loadedCategories.add(category)
}

let firstRun = true

export function initTrackingLoader () {
  onConsentChange(granted => {
    if (firstRun) {
      firstRun = false
      purgeUngrantedCookies(granted)
    }

    // A category that was loaded and is now denied can only be honoured by
    // clearing its cookies and reloading — scripts cannot be un-run.
    const revoked = Array.from(loadedCategories).some(category => !granted[category])

    if (revoked) {
      loadedCategories.clear()
      deleteCookies(ungrantedCookies(granted))
      window.location.reload()

      return
    }

    if (granted[CONSENT_CATEGORIES.ANALYTICS]) {
      loadMatomo()
      loadHotjar()
      loadedCategories.add(CONSENT_CATEGORIES.ANALYTICS)
    }

    if (granted[CONSENT_CATEGORIES.MARKETING]) {
      loadGoogleTagManager()
      loadEcosystemScripts()
      loadedCategories.add(CONSENT_CATEGORIES.MARKETING)
    }
  })
}
